import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useRef } from 'react';

import type { PolygonGeometry } from '@/lib/settings/zones';
import { useTheme } from '@/lib/theme';

/**
 * Draws the one polygon a delivery zone can have.
 *
 * Single-feature deliberately: `delivery_zones.area` is one geography, so a
 * second shape has nowhere to go -- drawing one replaces the first rather
 * than leaving the operator with a map that disagrees with the database.
 *
 * The dispatch board's map already renders saved areas; this is the editor,
 * and the two share nothing but the token because their needs diverge
 * immediately (one draws many zones read-only, the other edits exactly one).
 */

const LIGHT_STYLE = 'mapbox://styles/mapbox/light-v11';
const DARK_STYLE = 'mapbox://styles/mapbox/dark-v11';

/** Petaluma, so a store that has not drawn anything starts somewhere sensible. */
const FALLBACK_CENTER: [number, number] = [-122.6367, 38.2324];

export function ZoneAreaMap({
  value,
  onChange,
  center,
  disabled,
}: {
  value: PolygonGeometry | null;
  onChange: (polygon: PolygonGeometry | null) => void;
  center?: { latitude: number; longitude: number } | null;
  disabled?: boolean;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const draw = useRef<MapboxDraw | null>(null);
  const onChangeRef = useRef(onChange);
  /**
   * What the map is actually showing, so an external value does not fight the
   * editor. It starts null rather than at `value`: nothing is drawn at mount,
   * and seeding it with the prop made a cached polygon look already-drawn --
   * the effect then skipped it and the zone appeared to have no area.
   */
  const drawn = useRef<PolygonGeometry | null>(null);
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  onChangeRef.current = onChange;

  const publish = useCallback(() => {
    const instance = draw.current;

    if (!instance) return;

    const collection = instance.getAll();
    const feature = collection.features.at(-1);

    // Keep only the newest shape.
    if (collection.features.length > 1) {
      for (const extra of collection.features.slice(0, -1)) {
        if (extra.id) instance.delete(String(extra.id));
      }
    }

    if (feature?.geometry.type !== 'Polygon') {
      drawn.current = null;
      onChangeRef.current(null);

      return;
    }

    const geometry = feature.geometry as PolygonGeometry;

    drawn.current = geometry;
    onChangeRef.current(geometry);
  }, []);

  // Mount once: Mapbox owns its canvas, and re-creating the map on a prop
  // change would drop whatever the operator is mid-way through drawing.
  useEffect(() => {
    const token = import.meta.env.VITE_MAPBOX_TOKEN;

    if (!container.current || !token) return;

    mapboxgl.accessToken = token;

    const instance = new mapboxgl.Map({
      container: container.current,
      style: isDark ? DARK_STYLE : LIGHT_STYLE,
      center: center ? [center.longitude, center.latitude] : FALLBACK_CENTER,
      zoom: 11,
      attributionControl: false,
    });

    const drawControl = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      defaultMode: 'simple_select',
    });

    instance.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    instance.addControl(drawControl as unknown as mapboxgl.IControl, 'top-left');

    instance.on('draw.create', publish);
    instance.on('draw.update', publish);
    instance.on('draw.delete', publish);

    map.current = instance;
    draw.current = drawControl;

    // Reachable from the console in development, like the dispatch map, so a
    // shape that will not seed can be inspected rather than guessed at.
    if (import.meta.env.DEV) {
      (window as unknown as { __zoneDraw?: MapboxDraw }).__zoneDraw = drawControl;
    }

    /*
     * The dialog animates open, so the container is narrower when the map is
     * constructed than it is a frame later; without this the canvas keeps the
     * size it was born with and paints a blank strip.
     */
    const observer = new ResizeObserver(() => instance.resize());

    observer.observe(container.current);

    return () => {
      observer.disconnect();
      instance.remove();
      map.current = null;
      draw.current = null;
      /*
       * A new map starts with nothing drawn on it. Without this, React's
       * development double-mount seeded the first map, remembered it, and
       * then skipped seeding the map that actually stayed on screen.
       */
      drawn.current = null;
    };
  }, []);

  // Seed the stored polygon once it arrives (and after a zone switch).
  useEffect(() => {
    const instance = draw.current;
    const mapInstance = map.current;

    if (!instance || !mapInstance) return;
    if (value === drawn.current) return;

    drawn.current = value;

    if (!value) {
      instance.deleteAll();

      return;
    }

    const bounds = new mapboxgl.LngLatBounds();

    for (const ring of value.coordinates) {
      for (const point of ring) {
        if (point.length >= 2) bounds.extend([point[0] as number, point[1] as number]);
      }
    }

    /*
     * mapbox-gl-draw accepts `set()` and silently keeps nothing until its own
     * sources exist, and there is no event that reliably marks that moment --
     * `load`, `idle` and `styledata` all fire before it in some orders. So
     * this asks, checks whether the feature took, and tries again on the next
     * frame. Without it a zone that HAS an area opened with an empty map,
     * which reads as "nobody drew this yet".
     */
    let frame = 0;
    let attempts = 0;
    let cancelled = false;

    const attempt = () => {
      if (cancelled || !draw.current || !map.current) return;

      draw.current.set({
        type: 'FeatureCollection',
        features: [{ id: 'zone-area', type: 'Feature', properties: {}, geometry: value }],
      });

      if (draw.current.getAll().features.length > 0) {
        if (!bounds.isEmpty()) map.current.fitBounds(bounds, { padding: 40, duration: 0 });

        return;
      }

      attempts += 1;

      if (attempts < 60) frame = requestAnimationFrame(attempt);
    };

    attempt();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [value]);

  if (!import.meta.env.VITE_MAPBOX_TOKEN) {
    return (
      <p className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">
        Drawing a delivery area needs a Mapbox token (`VITE_MAPBOX_TOKEN`). ZIP codes still work
        without one.
      </p>
    );
  }

  return (
    <div className="relative">
      <div ref={container} className="h-80 w-full overflow-hidden rounded-lg border" />
      {disabled && <div className="absolute inset-0 rounded-lg bg-background/60" />}
    </div>
  );
}
